/**
 * Arquero runtime adapter — the §8.1 boundary.
 *
 * This is the only file in the engine that imports arquero. The
 * evaluator, the op registry, the type system, and the error model all
 * operate on the abstract `Dataset` value kind and never touch an arquero
 * `ColumnTable` directly. Top-level Dataset values entering verb apply
 * handlers are converted to a `ColumnTable` via `toTable`, processed by
 * arquero, and converted back via `fromTable`.
 *
 * `compileExpr` is the expression-context compiler: it walks an AST node
 * and produces a per-row JS function (`RowFn`) suitable for arquero's
 * `filter` / `derive` verbs. Each scalar/comparison/boolean op contributes
 * its own `compileExpr` method via `EagerOpMeta` — this file dispatches to
 * those methods.
 */

import type { ColumnTable } from 'arquero';
import { desc as aqDesc, escape as aqEscape, table as aqTable } from 'arquero';

import { NestedDatasetCellError, NotAnExpressionOpError, ShapeError, UnknownOpError } from '../errors.ts';
import { type AstInput, astShape, nodePos, opCallPos, type Spans } from '../normalize.ts';
import {
  type AggSpec,
  type ArqueroAggOpName,
  type CompileFn,
  type NameTable,
  opMeta,
  type RowFn,
  resolveOpCall,
} from '../op-meta.ts';
import { toDateValue } from '../ops/transform/date-utils.ts';
import { elemPath } from '../path.ts';
import { type Schema, unknownSchemaFor } from '../schema/types.ts';
import { type Cell, Dataset, MaterializedDataset } from '../value.ts';

/**
 * Convert a materialised engine `Dataset` to an arquero `ColumnTable`.
 * Tightened to `MaterializedDataset` in Phase 4-prep (sub-task 4-prep.2):
 * arquero only handles populated columns, so a `LazyDataset` must be
 * materialised before crossing this §8.1 boundary. Callers narrow via
 * `expectMaterialized` (verbs do so via the dispatch wrapper from
 * 4-prep.3 — verbs themselves never see lazy inputs).
 */
export const toTable = (ds: MaterializedDataset): ColumnTable => aqTable(ds.columns);

/**
 * Convert an arquero `ColumnTable` back to a `MaterializedDataset`.
 * Materialises every column to a plain `Cell[]` so the result has no
 * arquero-internal types leaking through. Cells may be `Scalar` (the
 * common case) or — since Phase 4-prep widened `Cell = Scalar | Dataset`
 * — a nested `Dataset` (the "TS reference" carrier shape).
 *
 * Return narrowed to `MaterializedDataset` in 4-prep.2: this function
 * always produces populated columns and is the canonical materialised
 * exit from the arquero boundary. Verbs (and `withTable`) carry that
 * tightness up the call chain.
 *
 * The caller must supply the result `Schema`. Per §8.4, the engine's
 * schema is authoritative — it is *not* inferred by inspecting the
 * arquero table, since arquero's type information is based on the
 * first row's JS runtime types and is poorer than the engine's
 * `ColumnType` system. Callers compute the output schema via the
 * relevant op's `propagateSchema` function (verbs) or via
 * `unknownSchemaFor(t.columnNames())` when no propagator applies.
 */
export const fromTable = (t: ColumnTable, schema: Schema): MaterializedDataset => {
  const columns: Record<string, readonly Cell[]> = {};
  for (const name of t.columnNames()) {
    columns[name] = [...t.array(name)] as Cell[];
  }
  return new MaterializedDataset(columns, schema);
};
/**
 * Verb-helper: round-trip a `MaterializedDataset` through arquero.
 * Every verb's apply converts the threaded Dataset to a `ColumnTable`,
 * applies an arquero op, then converts back. Centralises the §8.1
 * boundary crossing in one place.
 *
 * Output schema is synthesised as all-`unknown` from the result
 * table's column names; verbs with a registered `propagateSchema`
 * will replace this with a precise schema downstream
 * (see `runPropagateSchema` in `schema/infer.ts`).
 *
 * Lives here (in the §8.1 boundary file) so verb files don't
 * transitively import `builtins.ts` — that import-cycle would race
 * verb module registration against `builtins.ts`'s module-level
 * initialisation order. Moved from `builtins.ts` in Phase 4b.
 */
export const withTable = (ds: MaterializedDataset, op: (t: ColumnTable) => ColumnTable): MaterializedDataset => {
  const out = op(toTable(ds));
  return fromTable(out, unknownSchemaFor(out.columnNames()));
};
/** Join-type domain — shared by the `join` verb and this runtime adapter. */
export type JoinType = 'inner' | 'left' | 'right' | 'full';

/**
 * Per-`JoinType` arquero dispatch. Each entry calls the appropriate
 * `ColumnTable.join_*` method with `[leftKeys, rightKeys]` shape that
 * arquero accepts directly. Output schema is synthesised by the verb's
 * `propagateSchema`; this adapter only owns the join mechanics.
 */
const JOIN_FNS: Record<
  JoinType,
  (l: ColumnTable, r: ColumnTable, on: [readonly string[], readonly string[]]) => ColumnTable
> = {
  inner: (l, r, on) => l.join(r, on as [string[], string[]]),
  left: (l, r, on) => l.join_left(r, on as [string[], string[]]),
  right: (l, r, on) => l.join_right(r, on as [string[], string[]]),
  full: (l, r, on) => l.join_full(r, on as [string[], string[]]),
};

/**
 * Cross the §8.1 boundary for joins. The verb supplies the two
 * materialised datasets + the join keys + the join type; this
 * helper does the arquero round-trip and hands back a
 * `MaterializedDataset` with `unknownCol` types (verb's
 * `propagateSchema` refines them downstream).
 */
export const runtimeJoin = (
  left: MaterializedDataset,
  right: MaterializedDataset,
  on: [readonly string[], readonly string[]],
  type: JoinType,
): MaterializedDataset => {
  const lT = toTable(left);
  const rT = toTable(right);
  const out = JOIN_FNS[type](lT, rT, on);
  return fromTable(out, unknownSchemaFor(out.columnNames()));
};

/**
 * Compile an AST node to a per-row function. Walks:
 * - Literals (number/string/boolean/null/Date/Dataset) → constant fn.
 * - Vectors → array of compiled fns; row produces an array.
 * - Column refs (`{$sym: name}`) → `(row) => row[name]`. If the resolved
 *   cell is a `Dataset` (Phase 4-prep cell widening), the per-row
 *   function throws `NestedDatasetCellError` — expression-context verbs
 *   `filter` / `derive` do not yet have per-cell dispatch (lands in
 *   4-prep.5), so walking into a nested Dataset cell is an explicit
 *   error rather than a silent miscompare.
 * - Op calls → dispatch to the op's own `compileExpr` method via
 *   `opMeta`. Ops without a `compileExpr` method throw
 *   `NotAnExpressionOpError`.
 *
 * `spans` and `path` flow through so any thrown error carries the source
 * position of the offending node.
 */
export const compileExpr = (node: AstInput, ops: NameTable, spans: Spans, path: string): RowFn => {
  const shape = astShape(node);
  switch (shape.kind) {
    case 'primitive':
    case 'date':
    case 'dataset': {
      const v = shape.value;
      return () => v;
    }
    case 'vector': {
      const compiled = shape.elements.map((el, i) => compileExpr(el, ops, spans, elemPath(path, i)));
      return (row) => compiled.map((f) => f(row));
    }
    case 'symbolRef': {
      const { sym } = shape;
      const pos = nodePos(spans, path);
      return (row) => {
        const v = row[sym];
        if (v instanceof Dataset) {
          throw new NestedDatasetCellError(sym, pos);
        }
        return v;
      };
    }
    case 'opCall': {
      const { call } = shape;
      const { meta } = resolveOpCall(call.name, ops, opMeta, opCallPos(spans, path), 'op', UnknownOpError);
      // Gate on the hook's presence, not `evaluation`: a special form
      // may opt into expression context (e.g. `(col …)` needs raw AST
      // labels yet must compile inside `derive` / `filter`).
      if (meta.compileExpr === undefined) {
        throw new NotAnExpressionOpError(call.name, opCallPos(spans, path));
      }
      const compile: CompileFn = (childNode, childPath) => compileExpr(childNode as AstInput, ops, spans, childPath);
      return meta.compileExpr(call, compile, path);
    }
    case 'invalid':
      throw new ShapeError('expected an expression: literal, column reference, or op call', nodePos(spans, path));
  }
};
/**
 * Compiled form of an `AggSpec` — a pure JS reducer over a group's
 * rows. We deliberately do NOT use arquero's `rollup` op-namespace
 * here, even though sum/mean/min/max/count have arquero equivalents:
 * keeping the reducers uniformly manual makes the aggregate verb's
 * apply trivially simple (one code path) and side-steps the
 * arquero-rollup type machinery for expression objects (which Zod's
 * `$replace` doesn't play well with).
 *
 * The performance cost is acceptable for chat-scale datasets (≤10k
 * rows typical); if a future workflow needs faster aggregation, the
 * verb can swap individual reducers to use arquero's `op.*` directly
 * — but the verb apply stays clean meanwhile.
 */
export type AggReducer = (rows: readonly Record<string, unknown>[]) => unknown;
// Per-row pick reducers — SSOT for the four sites (col-agg first/last
// + col-pick first/last). `first`/`last` mean the first/last *present*
// value: null/undefined cells are skipped so a null boundary row
// (a gap-filled time bucket, a missing CDF sample) doesn't poison the
// aggregate. Defaults to null when the group has no present value.
const isPresentCell = (v: unknown): boolean => v !== null && v !== undefined;

const pickFirst =
  (col: string) =>
  (rows: readonly Record<string, unknown>[]): unknown =>
    rows.find((r) => isPresentCell(r[col]))?.[col] ?? null;
const pickLast =
  (col: string) =>
  (rows: readonly Record<string, unknown>[]): unknown =>
    rows.findLast((r) => isPresentCell(r[col]))?.[col] ?? null;

// SSOT numeric reducers — extract once, used by sum/mean/min/max.
// Filters out null/undefined AND empty/blank strings (note
// `Number('') === 0`, which would otherwise inject phantom zeros from
// missing CDF samples), then coerces to Number; NaN bubbles through so
// callers can detect "no valid input" by checking for NaN.
const validNumbers = (rows: readonly Record<string, unknown>[], col: string): readonly number[] => {
  return rows
    .map((r) => r[col])
    .filter((v) => isPresentCell(v) && !(typeof v === 'string' && v.trim() === ''))
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n));
};

const sumReducer =
  (col: string): AggReducer =>
  (rows) =>
    validNumbers(rows, col).reduce((s, n) => s + n, 0);

const meanReducer =
  (col: string): AggReducer =>
  (rows) => {
    const vals = validNumbers(rows, col);
    return vals.length === 0 ? null : vals.reduce((s, n) => s + n, 0) / vals.length;
  };

const minReducer =
  (col: string): AggReducer =>
  (rows) => {
    const vals = validNumbers(rows, col);
    return vals.length === 0 ? null : Math.min(...vals);
  };

const maxReducer =
  (col: string): AggReducer =>
  (rows) => {
    const vals = validNumbers(rows, col);
    return vals.length === 0 ? null : Math.max(...vals);
  };

const arrayReducer =
  (col: string): AggReducer =>
  (rows) =>
    rows.map((r) => r[col]);

/**
 * Per-op reducer factories, keyed by `ArqueroAggOpName`. Lookup-map
 * dispatch avoids the if-chain — adding a new column-agg op is one
 * row, not one branch.
 */
const COL_AGG_REDUCERS: Record<ArqueroAggOpName, (col: string) => AggReducer> = {
  sum: sumReducer,
  mean: meanReducer,
  min: minReducer,
  max: maxReducer,
  first: pickFirst,
  last: pickLast,
};

/**
 * Per-`AggSpec.kind` dispatch table. Each entry takes the spec and
 * returns the compiled reducer. Lookup-map dispatch is the SSOT for
 * spec-kind dispatch — keeps `compileAggSpec` a one-line lookup.
 */
const SPEC_KIND_REDUCERS: {
  readonly [Kind in AggSpec['kind']]: (spec: Extract<AggSpec, { kind: Kind }>) => AggReducer;
} = {
  'col-agg': (spec) => COL_AGG_REDUCERS[spec.op](spec.col),
  'row-count': () => (rows) => rows.length,
  'col-array': (spec) => arrayReducer(spec.col),
  'col-pick': (spec) => (spec.pick === 'first' ? pickFirst(spec.col) : pickLast(spec.col)),
};

/**
 * Translate an engine-side `AggSpec` into a pure-JS `AggReducer`.
 * Every variant maps to a reducer that consumes a group's row slice
 * (already partitioned by the verb's groupby logic) and returns a
 * single value.
 *
 * Null / NaN handling: numeric reducers (sum/mean/min/max) filter
 * out null, NaN, and empty/blank-string inputs (`Number('') === 0`
 * would otherwise inject phantom zeros). An all-invalid input set
 * returns `null` for mean/min/max and `0` for sum (matches
 * arithmetic intuition). The `first`/`last` pick reducers skip
 * null/undefined cells and return the first/last *present* value
 * (so a null boundary row — a gap-filled time bucket or a missing
 * CDF sample — doesn't poison the aggregate); falsy-but-present
 * values like `0`/`''` are preserved.
 */
export const compileAggSpec = (spec: AggSpec): AggReducer =>
  // biome-ignore lint/suspicious/noExplicitAny: dispatch table is per-kind narrowed; TS can't infer that
  (SPEC_KIND_REDUCERS[spec.kind] as (s: AggSpec) => AggReducer)(spec);
/**
 * Structural sort-key shape — `{column, direction}` records produced
 * by the `sort` verb (and any other consumer that needs arquero
 * orderby args). Typed structurally so this file does not depend on
 * the verb's internal `SortKey` alias; the verb supplies records
 * conforming to this shape.
 *
 * `dateAware` is set by the verb when the column is a `timestamp` /
 * `date` type: arquero's default comparator sorts string cells
 * lexically, which mis-orders mixed-offset / non-zero-padded / epoch
 * date representations. A `dateAware` key is ordered by parsed instant
 * instead, so the result is always chronological.
 */
export type SortKey = {
  readonly column: string;
  readonly direction: 'asc' | 'desc';
  readonly dateAware?: boolean;
};

/**
 * Translate engine-side `SortKey[]` into the variadic args arquero's
 * `table.orderby(…)` accepts. Asc keys pass through as bare column
 * names; desc keys wrap with arquero's `desc(name)` helper. A
 * `dateAware` key is ordered by parsed instant (an escaped row
 * expression over `toDateValue`) instead of arquero's lexical
 * default, so `timestamp`/`date` columns sort chronologically
 * regardless of cell representation (ISO string, epoch, `Date`).
 * Keeps the §8.1 boundary intact — verbs import this helper instead
 * of arquero directly.
 */
export const compileSortKeys = (keys: readonly SortKey[]): readonly (string | ReturnType<typeof aqDesc>)[] =>
  keys.map((k) => {
    if (!k.dateAware) {
      return k.direction === 'asc' ? k.column : aqDesc(k.column);
    }
    const col = k.column;
    const instant = aqEscape((d: Record<string, unknown>) => {
      const dt = toDateValue(d[col]);
      return dt === null ? null : dt.getTime();
    });
    return k.direction === 'asc' ? instant : aqDesc(instant);
  });
/**
 * Convert a `MaterializedDataset` to a row-oriented array of records.
 * Cell shape is preserved verbatim (`Cell = Scalar | Dataset`); the
 * function is pure JS, used by verbs that need row-iteration (the
 * aggregate verb's groupby, future time-bucket / pivot / fold verbs).
 *
 * `Record<string, unknown>` return type so manual reducers
 * (`AggReducer`) can consume the rows without each verb threading
 * its own `Cell` cast. The values are still `Cell` at runtime; the
 * looser type is for the reducer's convenience.
 *
 * Lives here (in the §8.1 boundary file) alongside `toTable` /
 * `fromTable` because it's structurally the same kind of helper —
 * a column-oriented ↔ row-oriented conversion — and the file already
 * owns dataset-row plumbing.
 */
export const materializedToRows = (ds: MaterializedDataset): readonly Record<string, unknown>[] => {
  const cols = Object.keys(ds.columns);
  if (cols.length === 0) {
    return [];
  }
  const firstCol = ds.columns[cols[0] as string];
  const n = firstCol?.length ?? 0;
  const rows: Record<string, unknown>[] = [];
  for (let i = 0; i < n; i++) {
    const row: Record<string, unknown> = {};
    for (const c of cols) {
      row[c] = ds.columns[c]?.[i];
    }
    rows.push(row);
  }
  return rows;
};

/**
 * A group produced by {@link groupRowsBy}: the representative
 * by-key values (used to populate the by-columns of the output row)
 * and the row slice belonging to the group (handed to per-group
 * reducers).
 */
export type RowGroup = {
  readonly representativeRow: Record<string, unknown>;
  readonly rows: readonly Record<string, unknown>[];
};

/**
 * Partition rows by the values of one or more columns. Empty
 * `byKeys` collapses the whole input to one group (global rollup
 * convention — `(aggregate (n (count)))` with no `:by` yields one
 * output row).
 *
 * The grouping key is `JSON.stringify(value)` joined with `\x00` —
 * stable across primitive types and arrays. Cells that are Datasets
 * would not group sensibly (reference identity), but the dispatch
 * wrapper's per-cell branch (Phase 4-prep sub-task 4-prep.3) lifts
 * those before the verb's apply runs, so this code path never sees
 * Dataset cells in by-key columns.
 */
export const groupRowsBy = (
  rows: readonly Record<string, unknown>[],
  byKeys: readonly string[],
): readonly RowGroup[] => {
  if (byKeys.length === 0) {
    return [{ representativeRow: {}, rows }];
  }
  const groupMap = new Map<string, { representativeRow: Record<string, unknown>; rows: Record<string, unknown>[] }>();
  for (const row of rows) {
    const key = byKeys.map((k) => JSON.stringify(row[k] ?? null)).join('\x00');
    let bucket = groupMap.get(key);
    if (bucket === undefined) {
      const representativeRow: Record<string, unknown> = {};
      for (const k of byKeys) {
        representativeRow[k] = row[k];
      }
      bucket = { representativeRow, rows: [] };
      groupMap.set(key, bucket);
    }
    bucket.rows.push(row);
  }
  return Array.from(groupMap.values());
};
